"use client";

import Icon from "@/components/ui/AppIcon";
import AppImage from "@/components/ui/AppImage";

type Product = {
  id: string;
  category: string;
  name: string;
  nameZh: string;
  brand: string;
  specs: string;
  image: string;
  alt: string;
  badge: string;
  badgeColor: string;
  moq: string;
};

interface ComparisonBarProps {
  selectedProducts: Product[];
  onRemove: (id: string) => void;
  onCompare: () => void;
  onClear: () => void;
}

export default function ComparisonBar({
  selectedProducts,
  onRemove,
  onCompare,
  onClear,
}: ComparisonBarProps) {
  if (selectedProducts.length === 0) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-navy shadow-navy-lg z-50 animate-slide-up">
      <div className="max-w-8xl mx-auto px-6 md:px-10 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Selected Products Display */}
          <div className="flex items-center gap-4 flex-1 overflow-x-auto no-scrollbar">
            <div className="flex items-center gap-2 text-navy font-bold text-sm whitespace-nowrap">
              <Icon name="ScaleIcon" size={20} />
              <span>Compare ({selectedProducts.length}/4)</span>
            </div>

            <div className="flex gap-3">
              {selectedProducts.map((product) => (
                <div
                  key={product.id}
                  className="flex items-center gap-2 bg-navy-50 border border-navy-100 rounded-xl px-3 py-2 min-w-fit group"
                >
                  <div className="relative w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                    <AppImage
                      src={product.image}
                      alt={product.alt}
                      fill
                      className="object-cover"
                      sizes="40px"
                    />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-bold text-navy truncate max-w-[120px]">
                      {product.name}
                    </span>
                    <span className="text-xs text-text-muted">{product.brand}</span>
                  </div>
                  <button
                    onClick={() => onRemove(product.id)}
                    className="ml-2 text-text-muted hover:text-orange transition-colors flex-shrink-0"
                    aria-label="Remove from comparison"
                  >
                    <Icon name="XMarkIcon" size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={onClear}
              className="text-text-muted hover:text-navy text-sm font-bold transition-colors"
            >
              Clear All
            </button>
            <button
              onClick={onCompare}
              disabled={selectedProducts.length < 2}
              className="flex items-center gap-2 bg-orange hover:bg-orange-dark disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-full font-black text-sm uppercase tracking-wider transition-all shadow-orange-glow disabled:shadow-none"
            >
              <Icon name="ArrowsRightLeftIcon" size={16} variant="solid" />
              Compare Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}